import React, { useState, useMemo, useEffect } from 'react';
import { CheckCircle, Archive, AlertTriangle, Loader2, Info, MessageSquare } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const MIN_LEN = 20;

const CloseReportDialog = ({ open, onOpenChange, report, defaultStatus = 'CONCLUIDA', initialMessage = '', onClosed }) => {
  const [status, setStatus] = useState(defaultStatus);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(defaultStatus || 'CONCLUIDA');
      setMessage(initialMessage || '');
    }
  }, [open, defaultStatus, initialMessage]);

  const remaining = useMemo(() => Math.max(0, MIN_LEN - message.trim().length), [message]);
  const canSubmit = remaining === 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await adminApi.updateReport(report.id, {
        status,
        closure_message: message.trim(),
      });
      toast.success(
        status === 'CONCLUIDA' ? 'Denúncia concluída' : 'Denúncia arquivada',
        { description: 'A mensagem final foi enviada ao denunciante.' }
      );
      onClosed?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao encerrar denúncia');
    } finally {
      setSaving(false);
    }
  };

  const isConclude = status === 'CONCLUIDA';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="close-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isConclude ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <Archive className="w-5 h-5 text-slate-600" />}
            Encerrar denúncia
          </DialogTitle>
          <DialogDescription>
            Ao encerrar uma denúncia, o denunciante recebe a mensagem que escreveres — não uma notificação genérica. É registada como mensagem oficial e enviada por email.
          </DialogDescription>
        </DialogHeader>

        {/* Copilot-style info panel */}
        <Alert className="bg-indigo-50 border-indigo-200">
          <Info className="w-4 h-4 text-indigo-600" />
          <AlertDescription>
            <div className="text-sm text-indigo-900 font-medium mb-2">Guia rápido: qual estado escolher?</div>
            <div className="overflow-hidden rounded-md border border-indigo-100 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-indigo-50 text-indigo-800">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Estado</th>
                    <th className="text-left px-3 py-2 font-medium">Significado</th>
                    <th className="text-left px-3 py-2 font-medium">Quando usar</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <tr className="border-t border-indigo-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                        <CheckCircle className="w-3.5 h-3.5" />
                        CONCLUÍDA
                      </span>
                    </td>
                    <td className="px-3 py-2">Investigação levada até ao fim com desfecho</td>
                    <td className="px-3 py-2">Caso legítimo tratado</td>
                  </tr>
                  <tr className="border-t border-indigo-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 text-slate-700 font-medium">
                        <Archive className="w-3.5 h-3.5" />
                        ARQUIVADA
                      </span>
                    </td>
                    <td className="px-3 py-2">Encerrada sem investigação/rejeitada</td>
                    <td className="px-3 py-2">Falsa denúncia, duplicada, ou rejeitada em workflow</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </AlertDescription>
        </Alert>

        {/* Status choice */}
        <div>
          <Label className="text-sm font-medium">Estado final</Label>
          <RadioGroup value={status} onValueChange={setStatus} className="mt-2 grid grid-cols-2 gap-3">
            <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition ${status === 'CONCLUIDA' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
              <RadioGroupItem value="CONCLUIDA" data-testid="close-status-concluida" />
              <div className="text-sm">
                <div className="font-medium flex items-center gap-1 text-emerald-800">
                  <CheckCircle className="w-4 h-4" />
                  Concluída
                </div>
                <div className="text-xs text-slate-600 mt-1">Investigação encerrada com desfecho</div>
              </div>
            </label>
            <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition ${status === 'ARQUIVADA' ? 'border-slate-400 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
              <RadioGroupItem value="ARQUIVADA" data-testid="close-status-arquivada" />
              <div className="text-sm">
                <div className="font-medium flex items-center gap-1 text-slate-800">
                  <Archive className="w-4 h-4" />
                  Arquivada
                </div>
                <div className="text-xs text-slate-600 mt-1">Sem investigação / rejeitada</div>
              </div>
            </label>
          </RadioGroup>
        </div>

        {/* Message */}
        <div>
          <Label className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Mensagem final ao denunciante
          </Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              isConclude
                ? 'Ex.: A sua denúncia foi investigada. Foram apuradas irregularidades e aplicadas as medidas correctivas X, Y e Z. Agradecemos a sua colaboração.'
                : 'Ex.: A sua denúncia foi analisada mas não reúne os requisitos para investigação porque… (indique motivo objectivo).'
            }
            className="mt-1 min-h-[140px]"
            data-testid="close-message-textarea"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-slate-500">
              {remaining > 0
                ? `Faltam ${remaining} caracteres (mínimo ${MIN_LEN}).`
                : `${message.trim().length} caracteres — pronto.`}
            </span>
            {remaining === 0 && (
              <span className="text-xs text-emerald-600 font-medium">✓ válido</span>
            )}
          </div>
        </div>

        {/* Preview */}
        {message.trim().length >= MIN_LEN && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="close-preview">
            <div className="text-xs text-slate-500 mb-1">O denunciante irá ver:</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{message.trim()}</div>
          </div>
        )}

        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800 text-sm">
            Depois de encerrada, esta denúncia só pode voltar a ser mexida usando <strong>"Reabrir denúncia"</strong> (motivo obrigatório).
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={isConclude ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-700 hover:bg-slate-800'}
            data-testid="confirm-close-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isConclude ? 'Concluir denúncia' : 'Arquivar denúncia'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CloseReportDialog;
